import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const BASE = `${environment.apiUrl2}/screener-queue`;
const QC_BASE = `${environment.apiUrl2}/qc-workflow`;
const USER_BASE = `${environment.apiUrl2}/user`;
const APPLICATIONS_BASE = `${environment.apiUrl2}/applications`;

/** Language an application was completed in — the value the queue tables tag
 *  rows with and filter on.
 *  Langue de rédaction de la candidature. */
export type QueueLanguage = 'en' | 'fr';

export interface ScreenerQueueFilter {
  cohortId?: string;
  drawId?: string;
  assignedToAdminId?: string;
  status?: string;
  phase?: 1 | 2;
  language?: QueueLanguage;
  page?: number;
  limit?: number;
  sortBy?: 'submittedAt' | 'assignedAt' | 'applicationNo' | 'status' | 'eligibility' | 'assignedTo';
  sortOrder?: 'asc' | 'desc';

  // Column filters of the queue table, ANDed with the filters above on the
  // server. The `column*` ones narrow a field a toolbar filter also narrows.
  // Filtres de colonne du tableau, combinés (ET) côté serveur.
  /** Part of the application number / Partie du numéro de candidature */
  applicationNo?: string;
  /** Words of the applicant's name / Mots du nom du candidat */
  applicant?: string;
  columnPhase?: 1 | 2;
  /** A draw the application sits in, Phase 1 or 2 / Un tirage de la candidature */
  columnDrawId?: string;
  /** The applicant's account language / Langue du compte du candidat */
  applicantLanguage?: QueueLanguage;
  communityStream?: string;
  /** Status of the phase the application is reviewed in / Statut de la phase de révision */
  applicationStatus?: string;
  flagStatus?: string;
  eligibilityStatus?: string;
  /** The decision the queue shows — latest on the timeline of its decision
   *  phase, else the stored one / La décision affichée */
  decisionMade?: string;
  decisionEmail?: 'sent' | 'notSent';
  columnStatus?: string;
  /** Part of the assigned screener's name / Partie du nom de l'examinateur */
  assignedTo?: string;
  /** Submitted on or after / before these instants / Bornes de soumission */
  submittedFrom?: string;
  submittedBefore?: string;
  /** AI chips, comma-separated — an application must match every one.
   *  Filtres IA séparés par des virgules ; tous doivent correspondre. */
  aiChips?: string;
}

/** Values present in the scoped queue for the column filters whose choices
 *  are not a fixed list. / Valeurs présentes pour les filtres à liste ouverte. */
export interface ScreenerQueueFilterOptions {
  applicationStatus: string[];
  eligibilityStatus: string[];
  decisionMade: string[];
}

/** One page of the screener queue. / Une page de la file d'examen. */
export interface ScreenerQueuePage {
  data: any[];
  /** Applications matching every filter, across all pages / Total, toutes pages */
  total: number;
  page: number;
  limit: number;
  filterOptions?: ScreenerQueueFilterOptions;
}

export interface AssignScreenerPayload {
  adminId: string;
  adminName: string;
  noteEn?: string;
  noteFr?: string;
}

/** Roles the backend accepts as the target of a manual assignment. Mirrors
 *  `MANUAL_ASSIGN_ROLES` in the backend's `screener-queue.service.ts` — keep the
 *  two in step, or the dropdown offers people the assign call rejects (or hides
 *  people it would accept).
 *  Rôles acceptés par le backend pour une assignation manuelle — à garder
 *  synchronisés avec `MANUAL_ASSIGN_ROLES` côté backend. */
export const MANUAL_ASSIGN_ROLES: readonly string[] = Object.freeze(['proScreener', 'proAdmin', 'proAdminQC', 'proManager', 'proQcReviewer', 'proCdReviewer', 'admin', 'superAdmin']);

/** A user who can be picked in the Assign Screener dialog.
 *  Utilisateur proposé dans le dialogue d'assignation. */
export interface AssignableScreener {
  _id: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role?: string;
  /** Full name, or the email when the profile has no name — users seeded from
   *  Cognito often lack one. / Nom complet, ou le courriel à défaut. */
  displayName: string;
}

export interface UnassignScreenerPayload {
  noteEn?: string;
  noteFr?: string;
}

export interface UpdateQueueStatusPayload {
  status: string;
}

export interface AutoDistributePayload {
  /** Activated PortalDraw ID to distribute (preferred). / Tirage activé à distribuer. */
  drawId?: string;
  /** Cohort (exam) ID fallback when drawId omitted. / Cohorte de repli. */
  cohortId?: string;
  screenerIds?: string[];
}

/** Activate applications from a PortalDraw into the screening queue.
 *  Activer les candidatures d'un tirage dans la file d'attente. */
export interface PullFromDrawPayload {
  drawId: string;
}

/** Submit a preliminary screening decision with referral routing.
 *  Soumettre une décision préliminaire avec acheminement de référence. */
export interface SubmitScreeningDecisionPayload {
  /** Omitted when routing to the Clinical Director — escalating asks the CD for
   *  a ruling rather than recording one, and the server rejects a decision-less
   *  submission for every other target.
   *  Omise lors d'une escalade au Directeur clinique. */
  decision?: string;
  commentEn?: string;
  commentFr?: string;
  flags?: string[];
  /** 'qc' = standard QC | 'manager' = manager sign-off | 'cd' = direct CD escalation */
  referralTarget: 'qc' | 'manager' | 'cd';
  referralReasonEn?: string;
  referralReasonFr?: string;
  /** Phase under review. The server derives it from the application when omitted. */
  phase?: 1 | 2;

  // Decision detail lists — each belongs to exactly one decision and is required
  // for it, matching the application-review decision form and the server-side
  // resolver both paths share.
  // Listes de details — chacune appartient a une seule decision.

  /** Required for 'More Information Required'. */
  reasons?: string[];
  /** Exam referrals required for 'Referral' — distinct from referralReasonEn/Fr,
   *  which explain the internal routing to the manager or CD. */
  referralReasons?: string[];
  /** Required for 'Conditional Offer'. */
  conditions?: string[];
  /** Required for 'Ineligible'. */
  ineligibleCriterias?: string[];
}

/** One entry in an application's internal staff review thread.
 *  `authorRole` is the UserRoleEnum value the author held when they wrote —
 *  it is what lets the panel label a Clinical Director's feedback as such.
 *  Une entrée du fil de commentaires internes du personnel. */
export interface ReviewComment {
  commentEn: string;
  commentFr?: string;
  authorId?: string;
  authorName?: string;
  authorRole?: string;
  createdAt: string;
}

/** POST /screener-queue/:id/review-comments — only `commentEn` is required.
 *  Seul `commentEn` est obligatoire. */
export interface AddReviewCommentPayload {
  commentEn: string;
  commentFr?: string;
}

export interface QCFinding {
  step: string;
  stepLabel: string;
  stepLabelFr: string;
  result: 'Confirmed' | 'Flag' | 'N/A';
  note?: string;
  noteFr?: string;
}

export interface SubmitQCReviewPayload {
  findings: QCFinding[];
  overallResult: 'Pass' | 'Pass with Notes' | 'Escalate to CD';
  phase: 1 | 2;
  escalationReason?: string;
  escalationReasonFr?: string;
}

export interface UpdateBriefDecisionPayload {
  recommendationStatus: string;
  manualBriefText?: string;
  manualBriefTextFr?: string;
}

/** POST /qc-workflow/:id/reopen — reopen a finalized decision. `reasonEn` is
 *  required — it becomes part of the permanent audit trail, not just UI copy.
 *  Rouvrir une décision finalisée — `reasonEn` est requis. */
export interface ReopenApplicationPayload {
  phase?: 1 | 2;
  reasonEn: string;
  reasonFr?: string;
}

/** B4 — Re-entry brief returned by the backend.
 *  Résumé de reprise retourné par le serveur. */
export interface ReentryBriefResult {
  generatedBy: 'system-ai' | 'system-rules';
  summaryEn: string;
  summaryFr: string;
  confidence: 'high' | 'medium' | 'low';
  sessionCount: number;
  daySpan: number;
  isHandoff: boolean;
  generatedAt?: string;
}

/** KYE-E02b — Run lifecycle outcomes recorded against every inbound webhook.
 *  Mirrors `KyeRunStatusEnum` on the server.
 *  KYE-E02b — Résultats de cycle de vie des exécutions Kye (serveur). */
export type KyeRunStatus = 'accepted' | 'dedup' | 'rejected_classification' | 'rejected_schema' | 'requested' | 'failed';

/** KYE-E02b — Aggregated Kye KPIs returned in the `kye` block of the
 *  screener-queue metrics endpoint. All values are undefined when
 *  `enabled === false` (feature flag off or computation degraded).
 *  KYE-E02b — KPI Kye agrégés retournés dans le bloc `kye`. */
export interface KyeKpiResult {
  enabled: boolean;
  window?: { fromIso: string; toIso: string; days: number };
  runs?: {
    total: number;
    byStatus: Record<KyeRunStatus, number>;
  };
  noteActions?: {
    accepted: number;
    dismissed: number;
    edited: number;
    copied: number;
    acceptRatio: number;
    dismissRatio: number;
    copyRatio: number;
  };
  dwellTimeMs?: { medianMs: number | null; p95Ms: number | null };
  /** Fraction (0-1) of applications whose latest Kye output is stale relative
   *  to the application document. Current-state signal, not window-bound.
   *  Fraction (0-1) des candidatures dont la sortie Kye est obsolète. */
  stalenessRate?: number;
}

/** KYE-E02b — Full response shape of `GET /screener-queue/metrics`. The
 *  pre-existing queue-health fields are unchanged; `kye` is additive.
 *  Forme complète de la réponse de l'endpoint de métriques. */
export interface ScreenerQueueMetricsResult {
  queueDepth: number;
  cdDepth: number;
  escalationRate: number;
  totalWithQCStatus: number;
  alerts: string[];
  alertsTriggered: boolean;
  thresholds: { cdDepth: number; escalationPct: number };
  generatedAt: string;
  kye: KyeKpiResult;
}

/** PRO Screener Queue & QC Workflow HTTP service.
 *  Service HTTP pour la file d'attente des examinateurs et le flux AQ PRO. */
@Injectable({ providedIn: 'root' })
export class ScreenerQueueHttpService {
  private readonly http = inject(HttpClient);

  // ─── Screener Queue ──────────────────────────────────────────────────────

  getQueue(filter: ScreenerQueueFilter = {}): Observable<ScreenerQueuePage> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    });
    return this.http.get<any>(BASE, { params });
  }

  getApplication(id: string): Observable<any> {
    return this.http.get<any>(`${BASE}/${id}`);
  }

  getStats(cohortId?: string): Observable<any[]> {
    let params = new HttpParams();
    if (cohortId) params = params.set('cohortId', cohortId);
    return this.http.get<any[]>(`${BASE}/stats`, { params });
  }

  /** KYE-E02 — Observability metrics including the additive `kye` KPI block.
   *  Backend RBAC restricts this to admin/QC/CD reviewer roles; callers must
   *  gate the call client-side as well to avoid noisy 403s.
   *  Métriques d'observabilité incluant le bloc additif `kye`. */
  getMetrics(cohortId?: string): Observable<ScreenerQueueMetricsResult> {
    let params = new HttpParams();
    if (cohortId) params = params.set('cohortId', cohortId);
    return this.http.get<ScreenerQueueMetricsResult>(`${BASE}/metrics`, { params });
  }

  /** Every user the backend will accept as a manual assignee, sorted by name.
   *  Tous les utilisateurs assignables manuellement, triés par nom. */
  getScreeners(): Observable<AssignableScreener[]> {
    return this.http.get<{ data: Omit<AssignableScreener, 'displayName'>[] }>(USER_BASE, { params: { role: MANUAL_ASSIGN_ROLES.join(',') } }).pipe(
      map((res) =>
        (res.data ?? [])
          .map((user) => ({
            ...user,
            displayName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      ),
    );
  }

  assignScreener(id: string, payload: AssignScreenerPayload): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/assign`, payload);
  }

  unassignScreener(id: string, payload: UnassignScreenerPayload = {}): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/unassign`, payload);
  }

  updateQueueStatus(id: string, payload: UpdateQueueStatusPayload): Observable<any> {
    return this.http.patch<any>(`${BASE}/${id}/status`, payload);
  }

  autoDistribute(payload: AutoDistributePayload): Observable<{ assigned: number; total: number }> {
    return this.http.post<any>(`${BASE}/auto-distribute`, payload);
  }

  /** Activate all applications in a PortalDraw into the screening queue (status = Pending).
   *  Activer toutes les candidatures d'un tirage dans la file d'attente (statut = Pending). */
  pullFromDraw(payload: PullFromDrawPayload): Observable<{ drawName: string; activated: number; message: string }> {
    return this.http.post<any>(`${BASE}/pull-from-draw`, payload);
  }

  /** Manually (re-)trigger KYE agents for every application in an activated draw.
   *  When force=true the dedup check is bypassed — every app gets a fresh run.
   *
   *  Déclencher manuellement (re-) les agents KYE pour toutes les candidatures
   *  d'un tirage activé. force=true contourne le dédup pour un run frais. */
  triggerKyeForDraw(drawId: string, force = false, retrospective = false): Observable<{ drawName: string; triggered: number; message: string }> {
    const params: string[] = [];
    if (force) params.push('force=true');
    if (retrospective) params.push('retrospective=true');
    const qs = params.length ? `?${params.join('&')}` : '';
    const url = `${BASE}/draws/${encodeURIComponent(drawId)}/trigger-kye${qs}`;
    return this.http.post<any>(url, {});
  }

  /** Submit a preliminary screening decision and route the application.
   *  Soumettre une décision préliminaire et acheminer la candidature. */
  submitScreeningDecision(id: string, payload: SubmitScreeningDecisionPayload): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/submit-decision`, payload);
  }

  /** B4 — Get or generate the screener re-entry brief for an application.
   *  Obtenir ou générer le résumé de reprise pour l'examinateur. */
  getReentryBrief(id: string): Observable<ReentryBriefResult> {
    return this.http.get<ReentryBriefResult>(`${BASE}/${id}/reentry-brief`);
  }

  // ─── Internal review comments ────────────────────────────────────────────

  /** Read the internal staff review thread, newest first.
   *  Lire le fil de commentaires internes, du plus récent au plus ancien. */
  getReviewComments(id: string): Observable<ReviewComment[]> {
    return this.http.get<ReviewComment[]>(`${BASE}/${encodeURIComponent(id)}/review-comments`);
  }

  /** Append a comment to the internal staff review thread.
   *  Ajouter un commentaire au fil de révision interne. */
  addReviewComment(id: string, payload: AddReviewCommentPayload): Observable<ReviewComment> {
    return this.http.post<ReviewComment>(`${BASE}/${encodeURIComponent(id)}/review-comments`, payload);
  }

  // ─── PA Manual Link ──────────────────────────────────────────────────────

  /** Manually link a PhysiciansApply record to a single application.
   *  Admin/QC/super-admin only (RBAC enforced server-side).
   *  When `candidateCode` is omitted the backend auto-detects it from the
   *  application's personal-information step data.
   *
   *  Lier manuellement un dossier PhysiciansApply à une candidature individuelle.
   *
   * @param applicationId  ProUserApplication ObjectId (path `:id`)
   * @param candidateCode  PA candidateCode2 to search for (optional — omit to auto-detect)
   */
  linkPaRecord(applicationId: string, candidateCode?: string): Observable<{ data: { linked: boolean; candidateCode: string; applicationNo: string } }> {
    return this.http.patch<{ data: { linked: boolean; candidateCode: string; applicationNo: string } }>(`${APPLICATIONS_BASE}/${encodeURIComponent(applicationId)}/link-pa`, candidateCode ? { candidateCode } : {});
  }

  // ─── QC Workflow ─────────────────────────────────────────────────────────

  getQCQueue(filter: { cohortId?: string; phase?: number; language?: QueueLanguage } = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined) params = params.set(k, String(v));
    });
    return this.http.get<any[]>(QC_BASE, { params });
  }

  getCDReviewQueue(filter: { cohortId?: string; language?: QueueLanguage } = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== null) params = params.set(k, String(v));
    });
    return this.http.get<any[]>(`${QC_BASE}/cd-review`, { params });
  }

  /** Load the manager review queue — applications with preliminary decisions awaiting final sign-off.
   *  Charger la file de révision du gestionnaire — candidatures avec décisions préliminaires en attente. */
  getManagerReviewQueue(filter: { cohortId?: string; phase?: number } = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined) params = params.set(k, String(v));
    });
    return this.http.get<any[]>(`${QC_BASE}/manager-review`, { params });
  }

  getQCApplication(id: string): Observable<any> {
    return this.http.get<any>(`${QC_BASE}/${id}`);
  }

  initiateQCReview(id: string): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/initiate`, {});
  }

  submitQCReview(id: string, payload: SubmitQCReviewPayload): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/submit`, payload);
  }

  updateBriefDecision(id: string, payload: UpdateBriefDecisionPayload): Observable<any> {
    return this.http.patch<any>(`${QC_BASE}/${id}/brief-decision`, payload);
  }

  /** Phase 1D — Finalize (publish) the most recent preliminary decision as the official final decision.
   *  Phase 1D — Finaliser (publier) la dernière décision préliminaire en tant que décision officielle finale. */
  finalizeDecision(id: string, payload: { noteEn?: string; noteFr?: string; phase?: 1 | 2 } = {}): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/finalize-decision`, payload);
  }

  /** Reopen a finalized decision for re-review — the inverse of finalizeDecision.
   *  Rouvrir une décision finalisée pour une nouvelle révision. */
  reopenApplication(id: string, payload: ReopenApplicationPayload): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/reopen`, payload);
  }

  // ─── B5: AI Document Validation / Validation IA des documents ────────────

  /** Trigger AI document validation (photo + ID) and persist results.
   *  Déclencher la validation IA des documents (photo + pièce d'identité) et persister les résultats. */
  triggerDocumentValidation(id: string): Observable<void> {
    return this.http.post<void>(`${BASE}/${id}/validate-documents`, {});
  }

  // ─── B6: Resume Template Compliance / Conformité du modèle de CV ─────────

  /** Trigger AI resume template compliance check and persist results.
   *  Déclencher la vérification de conformité du modèle de CV par IA et persister les résultats. */
  triggerResumeValidation(id: string): Observable<void> {
    return this.http.post<void>(`${BASE}/${id}/validate-resume`, {});
  }
}
